// 치과 「진료 후 질문」 수신함 데이터 훅 (2026-10-07 — ClinicRoadmapInbox.js 의 상태·핸들러를 공용으로 추출. 환자 자동관리 통합 화면 ①과 구 탭 컴포넌트가 함께 쓴다)
// 계약 docs/계약_질문로드맵_웹_2026-10-06.md §3. 서버 호출: listClinicRoadmapAnswers 1회(진입) + 더 보기(bucket+cursor) + 행 동작 3종.
//  - enabled=false 면 호출 0·data null (파일럿 아님 — useRoadmapPilot fail-closed 와 같은 원칙)
//  - 문구는 응답 labels 그대로. 콘솔에는 오류 코드만(환자명·답·번호 금지 — careRoadmapApi.describeRoadmapError 가 지킨다)
import { useCallback, useEffect, useState } from 'react';
import { getPatientAppClinicConsentShared, subscribePatientAppClinicConsent } from '../services/careAppApi';
import { isRoadmapPilotConsent } from './useRoadmapPilot';
import {
    listClinicRoadmapAnswers, acknowledgeRoadmapAnswer, stopRoadmapPhrase, revealRoadmapPhone, describeRoadmapError,
    INBOX_PAGE, fmtPhone,
} from '../services/careRoadmapApi';

export const ACK_TEXT = '확인 후 7일 뒤 답 내용이 삭제됩니다.';
export const ACK_REQUERY = (n) => `예정된 재질문 ${n}건을 취소했습니다`;
export const STOP_TEXT = '이 환자의 이 질문 일정에서 같은 질문을 다시 보내지 않습니다. 답은 그대로 남습니다.';
export const TRUNCATED_TEXT = '표시 한도(300건)를 넘어 일부 답이 보이지 않습니다. 확인 완료한 답은 7일 뒤 목록에서 빠집니다.';   // 법무 최종 대조 10-06
export const YES_TEXT = '예';   // T-5 — 서버 answer:'yes' 만 내려온다

// opts.gate = true (10-15 로딩 개선): 파일럿 판정(getPatientAppClinicConsent)을 먼저 기다리지 않고 답 목록 호출에 withGate 로 맡긴다(서버 1회).
//   enabled 는 이때 "플래그 켜짐"만 뜻한다. gateOk: null(판정 전)·true·false — 화면은 gateOk === true 일 때만 답을 보인다(fail-closed).
//   서버가 옛 판(gate 필드 없음)이면 종전 판정(getPatientAppClinicConsentShared)으로 되돌아간다.
export function useRoadmapInbox(enabled = true, opts = {}) {
    const withGate = opts.gate === true;
    const [gateOk, setGateOk] = useState(withGate ? null : true);
    const [data, setData] = useState(null);      // { summary, labels, sections, truncated, role }
    const [loading, setLoading] = useState(!!enabled);
    const [error, setError] = useState('');
    const [cursors, setCursors] = useState({});  // bucket → nextCursor(ms)|null. 초기 응답은 nextCursor 가 없어 행 수 100이면 더 보기 노출
    const [moreBusy, setMoreBusy] = useState('');
    const [rowBusy, setRowBusy] = useState('');  // `${fbId}:ack|stop` | `${pid}:phone`
    const [rowNote, setRowNote] = useState({});  // fbId → { type:'ok'|'err', text }
    const [phones, setPhones] = useState({});    // pid → 전체 번호(화면 표시만, 새로고침하면 사라짐)

    const load = useCallback(async () => {
        if (!enabled) { setData(null); setError(''); setLoading(false); setCursors({}); setPhones({}); if (withGate) setGateOk(false); return; }
        setLoading(true); setError('');
        try {
            const r = await listClinicRoadmapAnswers(withGate ? { withGate: true } : {});
            if (withGate) {
                let ok = r?.gate ? r.gate.pilotOk === true : null;
                if (ok === null) {   // 옛 서버 — 종전 판정
                    try { ok = isRoadmapPilotConsent(await getPatientAppClinicConsentShared()); }
                    catch (e) { console.error('[진료 후 질문 게이트] 위탁 확인 조회 실패(숨김 처리):', e?.code || e?.details?.code || e?.message || e); ok = false; }
                }
                setGateOk(ok);
                if (!ok) { setData(null); setCursors({}); setPhones({}); return; }
            }
            setData(r);
            setCursors({});
            setPhones({});
        } catch (e) {
            setError(describeRoadmapError(e, '질문 답을 불러오지 못했습니다.'));
        } finally { setLoading(false); }
    }, [enabled, withGate]);
    useEffect(() => { load(); }, [load]);
    // 위탁 확인 카드에서 확인·철회하면 다시 판정(useRoadmapPilot 과 같은 동작)
    useEffect(() => {
        if (!withGate || !enabled) return undefined;
        return subscribePatientAppClinicConsent(() => { load(); });
    }, [withGate, enabled, load]);

    const hasMore = useCallback((bucket) => {
        const rows = data?.sections?.[bucket] || [];
        if (rows.length === 0) return false;
        return cursors[bucket] != null ? true : (cursors[bucket] === undefined && rows.length >= INBOX_PAGE);
    }, [data, cursors]);

    const loadMore = useCallback(async (bucket) => {
        if (!data || moreBusy) return;
        const rows = data.sections?.[bucket] || [];
        const cursor = cursors[bucket] != null ? cursors[bucket] : (rows.length ? rows[rows.length - 1].answeredAt : null);
        if (!cursor) return;
        setMoreBusy(bucket);
        try {
            const r = await listClinicRoadmapAnswers({ bucket, cursor, limit: INBOX_PAGE });
            const add = r.sections?.[bucket] || [];
            setData(d => ({ ...d, sections: { ...d.sections, [bucket]: [...rows, ...add.filter(x => !rows.some(y => y.fbId === x.fbId))] }, truncated: d.truncated || r.truncated }));
            setCursors(c => ({ ...c, [bucket]: r.nextCursor == null ? null : r.nextCursor }));
        } catch (e) {
            setError(describeRoadmapError(e, '더 불러오지 못했습니다.'));
        } finally { setMoreBusy(''); }
    }, [data, moreBusy, cursors]);

    const patchRow = useCallback((fbId, patch) => setData(d => {
        if (!d) return d;
        const sections = {};
        for (const k of Object.keys(d.sections || {})) sections[k] = (d.sections[k] || []).map(r => r.fbId === fbId ? { ...r, ...patch } : r);
        return { ...d, sections };
    }), []);

    const ack = useCallback(async (row) => {
        if (rowBusy) return;
        setRowBusy(`${row.fbId}:ack`);
        try {
            const r = await acknowledgeRoadmapAnswer(row.fbId);
            patchRow(row.fbId, { status: 'acknowledged', ackAt: Date.now(), deleteAt: r.deleteAt || row.deleteAt });
            setRowNote(n => ({ ...n, [row.fbId]: { type: 'ok', text: `${ACK_TEXT}${r.requeryCancelled > 0 ? ` ${ACK_REQUERY(r.requeryCancelled)}` : ''}` } }));
            // 구역 재배치(answered → done)는 새로고침 때 반영 — 행은 제자리에서 상태만 바꿔 혼란을 줄인다
            setData(d => d ? { ...d, summary: { ...d.summary, toCheckCount: Math.max(0, (d.summary?.toCheckCount || 1) - 1) } } : d);
        } catch (e) {
            setRowNote(n => ({ ...n, [row.fbId]: { type: 'err', text: describeRoadmapError(e, '확인 완료 처리를 하지 못했습니다.') } }));
        } finally { setRowBusy(''); }
    }, [rowBusy, patchRow]);

    const stop = useCallback(async (row) => {
        if (rowBusy) return;
        setRowBusy(`${row.fbId}:stop`);
        try {
            const r = await stopRoadmapPhrase(row.fbId);
            patchRow(row.fbId, { phraseStopped: true });
            setRowNote(n => ({ ...n, [row.fbId]: { type: 'ok', text: `${STOP_TEXT}${r.requeryCancelled > 0 ? ` ${ACK_REQUERY(r.requeryCancelled)}` : ''}` } }));
        } catch (e) {
            setRowNote(n => ({ ...n, [row.fbId]: { type: 'err', text: describeRoadmapError(e, '처리하지 못했습니다.') } }));
        } finally { setRowBusy(''); }
    }, [rowBusy, patchRow]);

    const reveal = useCallback(async (row) => {
        if (rowBusy) return;
        setRowBusy(`${row.pid}:phone`);
        try {
            const r = await revealRoadmapPhone(row.pid);
            setPhones(p => ({ ...p, [row.pid]: r.phoneE164 ? fmtPhone(r.phoneE164) : (r.phoneLast4 ? `***-${r.phoneLast4}` : '번호 없음') }));
        } catch (e) {
            setRowNote(n => ({ ...n, [row.fbId]: { type: 'err', text: describeRoadmapError(e, '전화번호를 보지 못했습니다.') } }));
        } finally { setRowBusy(''); }
    }, [rowBusy]);

    return { data, loading, error, cursors, moreBusy, rowBusy, rowNote, phones, load, loadMore, hasMore, ack, stop, reveal, gateOk };
}

export default useRoadmapInbox;
